import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, RefreshCw, AlertTriangle, Clock, TrendingUp, Layers, LogOut, Search, Download, Mail, MailOpen } from "lucide-react";
import { useI18n } from "../i18n/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { listRequests, updateRequest, deleteRequest, getStats, seedDemo } from "../lib/api";
import { RequestDetail } from "../components/admin/RequestDetail";
import { toast } from "sonner";

const STATUSES = ["new", "being_arranged", "options_ready", "awaiting_confirmation", "confirmed", "in_progress", "completed", "cancelled"];
const CATEGORIES = ["aviation", "yacht", "chauffeur", "villa", "dining", "nightlife", "security", "experience", "event", "lifestyle", "other"];
const URGENCIES = ["urgent", "within_3_hours", "today", "whenever"];

const urgencyColor = {
  urgent: "text-red-400 border-red-400/40",
  within_3_hours: "text-orange-300 border-orange-300/40",
  today: "text-vm-accent border-vm-accent/40",
  whenever: "text-vm-muted border-vm-border",
};

const toCsv = (rows) => {
  const cols = ["created_at", "name", "phone", "email", "category", "urgency", "status", "assigned_concierge", "location", "date", "supplier_cost", "client_price", "confirmation_number", "message", "internal_notes"];
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  return [cols.join(","), ...rows.map((r) => cols.map((c) => esc(r[c])).join(","))].join("\n");
};

export default function Admin() {
  const { t } = useI18n();
  const { logout } = useAuth();
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [fCategory, setFCategory] = useState("");
  const [fUrgency, setFUrgency] = useState("");
  const [onlyUnread, setOnlyUnread] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([listRequests(), getStats()]);
      setRows(r);
      setStats(s);
    } catch {
      toast.error("Failed to load operations data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const applyLocal = (id, patch) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const patch = async (id, patchObj, silent = false) => {
    applyLocal(id, patchObj);
    try {
      await updateRequest(id, patchObj);
      if (!silent) toast.success("Saved");
      const s = await getStats();
      setStats(s);
    } catch {
      toast.error("Update failed");
      load();
    }
  };

  const openRow = (r) => {
    setSelected(r);
    if (!r.read) patch(r.id, { read: true }, true);
  };

  const remove = async (id) => {
    try {
      await deleteRequest(id);
      setRows((rs) => rs.filter((r) => r.id !== id));
      setSelected(null);
      toast.success("Request deleted");
      setStats(await getStats());
    } catch {
      toast.error("Delete failed");
    }
  };

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (fStatus && r.status !== fStatus) return false;
      if (fCategory && r.category !== fCategory) return false;
      if (fUrgency && r.urgency !== fUrgency) return false;
      if (onlyUnread && r.read) return false;
      if (!needle) return true;
      return [r.name, r.email, r.phone, r.location, r.message, r.assigned_concierge, r.confirmation_number]
        .some((v) => String(v || "").toLowerCase().includes(needle));
    });
  }, [rows, q, fStatus, fCategory, fUrgency, onlyUnread]);

  const exportCsv = () => {
    const blob = new Blob([toCsv(filtered)], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `velmonte-requests-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const doSeed = async () => {
    const res = await seedDemo();
    toast[res.seeded ? "success" : "info"](res.seeded ? `Loaded ${res.count} demo requests` : "Data already present");
    load();
  };

  const cards = stats ? [
    { label: t("admin.total"), value: stats.total, icon: Layers },
    { label: "Unread", value: stats.unread ?? 0, icon: Mail, accent: (stats.unread ?? 0) > 0 },
    { label: t("admin.today"), value: stats.today, icon: Clock },
    { label: t("admin.urgent"), value: stats.urgent, icon: AlertTriangle },
    { label: t("admin.revenue"), value: `€${stats.revenue.toLocaleString()}`, icon: TrendingUp },
    { label: t("admin.margin"), value: `€${stats.margin.toLocaleString()}`, icon: TrendingUp },
  ] : [];

  return (
    <div className="min-h-screen bg-vm-bg text-vm-text" data-testid="admin-page">
      <div className="border-b border-vm-border">
        <div className="max-w-[1500px] mx-auto px-5 md:px-10 py-5 md:h-20 flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="font-serif text-2xl tracking-[0.2em]">VELMONTÉ</div>
            <p className="vm-label mt-0.5">{t("admin.subtitle")}</p>
          </div>
          <div className="flex items-center gap-4 md:gap-5 flex-wrap">
            <button onClick={load} className="text-xs tracking-widest uppercase text-vm-text2 hover:text-vm-text inline-flex items-center gap-2" data-testid="admin-refresh-button">
              <RefreshCw strokeWidth={1.5} size={15} /> Refresh
            </button>
            <button onClick={exportCsv} className="text-xs tracking-widest uppercase text-vm-text2 hover:text-vm-text inline-flex items-center gap-2" data-testid="admin-export-button">
              <Download strokeWidth={1.5} size={15} /> CSV
            </button>
            <button onClick={doSeed} className="vm-btn-ghost px-5 py-2.5 text-xs tracking-widest uppercase" data-testid="admin-seed-button">
              {t("admin.seed")}
            </button>
            <Link to="/" className="text-xs tracking-widest uppercase text-vm-text2 hover:text-vm-text inline-flex items-center gap-2" data-testid="admin-back-link">
              <ArrowLeft strokeWidth={1.5} size={15} /> {t("admin.back")}
            </Link>
            <button onClick={logout} className="text-xs tracking-widest uppercase text-vm-muted hover:text-vm-text inline-flex items-center gap-2" data-testid="admin-logout-button">
              <LogOut strokeWidth={1.5} size={15} /> Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1500px] mx-auto px-5 md:px-10 py-8 md:py-10">
        <h1 className="font-serif text-4xl md:text-5xl font-light mb-8">{t("admin.title")}</h1>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-vm-border mb-10 border border-vm-border">
          {cards.map((c, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className="bg-vm-bg p-5 md:p-6" data-testid={`stat-card-${i}`}>
              <c.icon strokeWidth={1} size={18} className={c.accent ? "text-red-400 mb-4" : "text-vm-accent mb-4"} />
              <div className={`font-serif text-3xl font-light ${c.accent ? "text-red-400" : ""}`}>{c.value}</div>
              <div className="vm-label mt-2">{c.label}</div>
            </motion.div>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="relative flex-1 min-w-[220px]">
            <Search strokeWidth={1.2} size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-vm-muted" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, phone, email, location, text…"
              className="vm-input pl-10" data-testid="admin-search-input" />
          </div>
          <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} className="vm-input !w-auto capitalize flex-none" data-testid="filter-status">
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</option>)}
          </select>
          <select value={fCategory} onChange={(e) => setFCategory(e.target.value)} className="vm-input !w-auto capitalize flex-none" data-testid="filter-category">
            <option value="">All services</option>
            {CATEGORIES.map((c) => <option key={c} value={c} className="capitalize">{c}</option>)}
          </select>
          <select value={fUrgency} onChange={(e) => setFUrgency(e.target.value)} className="vm-input !w-auto capitalize flex-none" data-testid="filter-urgency">
            <option value="">All urgencies</option>
            {URGENCIES.map((u) => <option key={u} value={u} className="capitalize">{u.replace(/_/g, " ")}</option>)}
          </select>
          <button onClick={() => setOnlyUnread((v) => !v)}
            className={`px-4 py-3 text-xs tracking-widest uppercase border ${onlyUnread ? "border-vm-accent text-vm-accent" : "border-vm-border text-vm-muted"}`}
            data-testid="filter-unread-toggle">
            Unread only
          </button>
        </div>

        <p className="vm-label mb-4" data-testid="admin-result-count">{filtered.length} of {rows.length} requests</p>

        <div className="border border-vm-border overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]" data-testid="requests-table">
            <thead>
              <tr className="border-b border-vm-border text-left">
                {["", t("admin.client"), t("admin.service"), t("admin.urgencyCol"), t("admin.concierge"), t("admin.status"), t("admin.created")].map((h, i) => (
                  <th key={i} className="vm-label font-medium px-5 py-4">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} onClick={() => openRow(r)}
                  className={`border-b border-vm-border/60 hover:bg-white/[0.03] transition-colors align-top cursor-pointer ${r.read ? "" : "bg-vm-accent/[0.04]"}`}
                  data-testid={`request-row-${r.id}`}>
                  <td className="px-5 py-5" onClick={(e) => { e.stopPropagation(); patch(r.id, { read: !r.read }, true); }}>
                    <button className={r.read ? "text-vm-muted hover:text-vm-accent" : "text-vm-accent"} data-testid={`toggle-read-${r.id}`}
                      title={r.read ? "Mark as unread" : "Mark as read"}>
                      {r.read ? <MailOpen strokeWidth={1.3} size={16} /> : <Mail strokeWidth={1.3} size={16} />}
                    </button>
                  </td>
                  <td className="px-5 py-5">
                    <div className={`${r.read ? "text-vm-text" : "text-vm-text font-semibold"}`}>{r.name}</div>
                    <div className="text-vm-muted text-xs mt-1">{r.phone || r.email || "—"}</div>
                    {r.location && <div className="text-vm-muted text-xs mt-0.5">{r.location}</div>}
                  </td>
                  <td className="px-5 py-5 max-w-md">
                    <div className="text-vm-accent text-xs uppercase tracking-wider mb-1">{t(`request.categories.${r.category}`)}</div>
                    <div className="text-vm-text2 leading-relaxed line-clamp-2">{r.message || "—"}</div>
                  </td>
                  <td className="px-5 py-5">
                    <span className={`inline-block text-[0.65rem] uppercase tracking-wider px-2.5 py-1 border ${urgencyColor[r.urgency] || urgencyColor.whenever}`}>
                      {t(`request.urgencies.${r.urgency}`)}
                    </span>
                  </td>
                  <td className="px-5 py-5 text-vm-text2 text-xs">{r.assigned_concierge || "—"}</td>
                  <td className="px-5 py-5" onClick={(e) => e.stopPropagation()}>
                    <select value={r.status} onChange={(e) => patch(r.id, { status: e.target.value }, true)}
                      className="bg-transparent border border-vm-border px-2 py-1.5 text-xs text-vm-text2 focus:border-vm-accent capitalize" data-testid={`status-select-${r.id}`}>
                      {STATUSES.map((s) => <option key={s} value={s} className="bg-vm-surface capitalize">{s.replace(/_/g, " ")}</option>)}
                    </select>
                  </td>
                  <td className="px-5 py-5 text-vm-muted text-xs whitespace-nowrap">
                    {new Date(r.created_at).toLocaleDateString()}<br />
                    {new Date(r.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!loading && filtered.length === 0 && (
            <div className="py-20 text-center text-vm-muted" data-testid="admin-empty">{t("admin.noData")}</div>
          )}
        </div>
      </div>

      {selected && (
        <RequestDetail
          request={rows.find((r) => r.id === selected.id) || selected}
          onClose={() => setSelected(null)}
          onSave={async (id, patchObj) => { await patch(id, patchObj); }}
          onDelete={remove}
        />
      )}
    </div>
  );
}
